import Grid2 from '@mui/material/Grid2'
import Icon, { iconClasses } from '@mui/material/Icon'
import IconButton, { iconButtonClasses } from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import { listItemSecondaryActionClasses } from '@mui/material/ListItemSecondaryAction'
import ListItemText, { ListItemTextProps, listItemTextClasses } from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'

import CustomTooltip from '@/app/component/atom/tooltip'
import { ContentFlow } from '@/app/module/campaigns/types/common'

import { CampaignAddButton } from './helpers'
import { hasWaFlowPageValidationErrors } from './wa-flow-editor-utils'

type Props = {
  canAddPage: boolean
  canRemovePage: boolean
  currentPageId?: string
  flow: ContentFlow
  pageLabel: string
  pageMissingHelperText: string

  onPageAdd: () => void
  onPageRemove: (pageId: string) => void
  onPageSelect: (pageId: string) => void
}

const WaFlowEditorPages: React.FC<Props> = ({
  canAddPage,
  canRemovePage,
  currentPageId,
  flow,
  pageLabel,
  pageMissingHelperText,
  onPageAdd,
  onPageRemove,
  onPageSelect,
}) => {
  const hasPages = flow.pages.length > 0

  return (
    <Grid2 size={{ xs: 12, md: 4 }}>
      <Stack spacing={2}>
        <Typography variant="h6">{pageLabel}</Typography>
        <PagesList>
          {flow.pages.map((page, index) => {
            const isSelected = page.id === currentPageId
            const hasErrors = hasWaFlowPageValidationErrors(page)

            return (
              <StyledListItem
                disablePadding
                divider
                key={page.id}
                secondaryAction={
                  <Stack alignItems="center" direction="row" spacing={1}>
                    {hasErrors && (
                      <CustomTooltip title="This page has invalid configuration">
                        <Icon aria-hidden={false} color="warning" role="img" tabIndex={0}>
                          warning_amber
                        </Icon>
                      </CustomTooltip>
                    )}
                    <IconButton disabled={!canRemovePage} size="small" onClick={() => onPageRemove(page.id)}>
                      <Icon>close</Icon>
                    </IconButton>
                  </Stack>
                }
              >
                <ListItemButton
                  aria-description={hasErrors ? 'This page has invalid configuration' : undefined}
                  aria-label={page.title || `Page ${index + 1}`}
                  selected={isSelected}
                  sx={{ minWidth: 0, pr: hasErrors ? 9 : 6 }}
                  onClick={() => onPageSelect(page.id)}
                >
                  <StyledListItemText
                    isSelected={isSelected}
                    primary={page.title || `Page ${index + 1}`}
                    slotProps={{
                      primary: {
                        title: page.title || `Page ${index + 1}`,
                      },
                    }}
                  />
                </ListItemButton>
              </StyledListItem>
            )
          })}
        </PagesList>
        {!hasPages && <Typography color="textSecondary">{pageMissingHelperText}</Typography>}
        <Stack alignItems="flex-end" direction="row" justifyContent="flex-end">
          <CampaignAddButton
            disabled={!canAddPage}
            icon="add"
            text="Add page"
            tooltip={!canAddPage ? 'You have reached the maximum number of pages.' : ''}
            onClick={onPageAdd}
          />
        </Stack>
      </Stack>
    </Grid2>
  )
}

const PagesList = styled(List)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  padding: 0,
}))

const StyledListItem = styled(ListItem)(({ theme }) => ({
  paddingRight: 0,

  [`& .${listItemSecondaryActionClasses.root}`]: {
    right: theme.spacing(1),

    [`& .${iconButtonClasses.root}`]: {
      padding: theme.spacing(0.5),

      [`& .${iconClasses.root}`]: {
        fontSize: '1.25rem',
      },
    },
  },
}))

const StyledListItemText = styled(ListItemText)<ListItemTextProps & { isSelected: boolean }>(
  ({ theme, isSelected }) => ({
    minWidth: 0,
    [`& .${listItemTextClasses.primary}`]: {
      color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  }),
)

export default WaFlowEditorPages

export type { Props as WaFlowEditorPagesProps }
